import { StyleType, wixFont, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  StyleParamType,
  createStylesParams,
  createStylesParam,
  wixFontParam,
  wixColorParam,
} from '@wix/tpa-settings';

export type IStylesParams = {
  isBlogComments: StyleParamType.Boolean;
  commentsSpacing: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  replyLeftPadding: StyleParamType.Number;
  commentCountFont: StyleParamType.Font;
  commentCountMobileFontSize: StyleParamType.Number;
  commentCountColor: StyleParamType.Color;
  sortingFont: StyleParamType.Font;
  sortingMobileFontSize: StyleParamType.Number;
  sortingColor: StyleParamType.Color;
  headerDividerColor: StyleParamType.Color;
  headerDividerWidth: StyleParamType.Number;
  commentBoxPlaceholderColor: StyleParamType.Color;
  commentBoxPlaceholderMobileFontSize: StyleParamType.Number;
  commentBoxPlaceholderFont: StyleParamType.Font;
  closedCommentBoxFillColor: StyleParamType.Color;
  closedCommentBoxBorderColor: StyleParamType.Color;
  closedCommentBoxBorderWidth: StyleParamType.Number;
  closedCommentBoxCornerRadius: StyleParamType.Number;
  openCommentBoxTextColor: StyleParamType.Color;
  pluginsIconColor: StyleParamType.Color;
  openCommentBoxFillColor: StyleParamType.Color;
  openCommentBoxBorderColor: StyleParamType.Color;
  buttonColor: StyleParamType.Color;
  buttonFont: StyleParamType.Font;
  buttonTextColor: StyleParamType.Color;
  buttonCornerRadius: StyleParamType.Number;
  commentListCommentFont: StyleParamType.Font;
  commentListCommentMobileFontSize: StyleParamType.Number;
  commentListCommentTextColor: StyleParamType.Color;
  commentListSecondaryFont: StyleParamType.Font;
  commentListSecondaryMobileFontSize: StyleParamType.Number;
  commentListSecondaryTextColor: StyleParamType.Color;
  commentListUsernameFont: StyleParamType.Font;
  commentListUsernameMobileFontSize: StyleParamType.Number;
  commentListUsernameTextColor: StyleParamType.Color;
  commentListBadgeColor: StyleParamType.Color;
  commentListHighlightColor: StyleParamType.Color;
  markedCommentCornerRadius: StyleParamType.Number;
  actionsFont: StyleParamType.Font;
  actionsTextColor: StyleParamType.Color;
  actionsIconColor: StyleParamType.Color;
  commentListDividerColor: StyleParamType.Color;
  widgetBackgroundColor: StyleParamType.Color;
  widgetBorderColor: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetCornerRadius: StyleParamType.Number;
  filledRatingStar: StyleParamType.Color;
  emptyRatingStar: StyleParamType.Color;
  ratingInfoFont: StyleParamType.Font;
  ratingInfoColor: StyleParamType.Color;
  headerButtonFont: StyleParamType.Font;
  headerButtonColor: StyleParamType.Color;
  headerButtonRadius: StyleParamType.Number;
};

const isBlogComments = createStylesParam('isBlogComments', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

export default createStylesParams<IStylesParams>({
  isBlogComments,
  commentCountFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 20 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-heading-2-font'),
    },
  },
  commentCountMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
    },
  },
  commentCountColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  sortingFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  sortingMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
    },
  },
  sortingColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  headerButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  headerButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
    },
  },
  headerButtonRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  headerDividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  headerDividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  commentBoxPlaceholderMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
    },
  },
  commentBoxPlaceholderFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  commentBoxPlaceholderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-3-color'),
    },
  },
  closedCommentBoxFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
    },
  },
  closedCommentBoxBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  closedCommentBoxBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  closedCommentBoxCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  openCommentBoxTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  pluginsIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  openCommentBoxFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
    },
  },
  openCommentBoxBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  buttonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
    },
  },
  buttonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  buttonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
    },
  },
  buttonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  commentListBadgeColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
    },
  },
  commentListHighlightColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
    },
  },
  markedCommentCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  commentListCommentFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  commentListCommentMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 14,
      },
    },
  },
  commentListCommentTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  commentListSecondaryFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  commentListSecondaryMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 12,
      },
    },
  },
  commentListSecondaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-3-color'),
    },
  },
  commentListUsernameFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  commentListUsernameMobileFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 12,
      },
    },
  },
  commentListUsernameTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  actionsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
  },
  actionsTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  actionsIconColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  commentListDividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  widgetBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-primary-background-color'),
    },
  },
  widgetBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
  widgetBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  widgetCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  commentsSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 16,
      },
    },
  },
  sidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) => {
      return getStyleParamValue(isBlogComments) ? 0 : 20;
    },
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  replyLeftPadding: {
    type: StyleParamType.Number,
    getDefaultValue: ({ isMobile }) => (isMobile ? 20 : 48),
    metadata: {
      type: StyleType.number,
      defaultValue: {
        type: 'static',
        value: 48,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  filledRatingStar: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-links-and-actions-color'),
    },
  },
  emptyRatingStar: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-system-disabled-color'),
    },
  },
  ratingInfoFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-S'),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-3-font'),
    },
  },
  ratingInfoColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
  },
});
